import {
  Label,
  LabelEdge,
  LabelSuggestion,
  LabelSuggestionType,
  LabelType,
  LabelWithEdges,
  LabelWithStateAndSuggestions,
  MlSuggestion,
  PotentialSuggestion,
} from '@/model';
import api from '@/rest-api';
import uiConfig from '@/store/ui-config';
import {
  notEmpty,
  sortByExplicit,
  uniqueIndex,
  uniquelyIndexed,
} from '@/util/array-util';
import { editDistance } from '@/util/diff';
import { memoize } from '@/util/mem-util';
import Vue from 'vue';

const FallbackLabelTypeOrder = [
  'Workflow',
  'Level',
  'Area',
  'ThreatCategory',
  'Threat',
  'Theme',
  'Quantifiability',
  'Insights ',
  'Customer',
  'Industry',
  'LegalForm',
  'Product',
  'Transaction',
  'Channel',
  'Geography',
  'Sub-Regions',
];

export function getLabelTypeOrder(): string[] {
  const { labelTypes } = labels.state;
  if (labelTypes.length === 0) {
    return FallbackLabelTypeOrder;
  }
  return [...labelTypes]
    .sort((a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999))
    .map((lt) => lt.code);
}

export const LabelTypeOrder = FallbackLabelTypeOrder;

export const AdvancedLabelTypeGroupingAndOrder = [[
  'Workflow',
  'Theme',
  'Threat',
  'Customer',
  'Industry',
  'LegalForm',
  'Product',
  'Transaction',
  'Channel',
  'Geography',
  'Sub-Regions',
], [
  'Level',
  'Area',
  'ThreatCategory',
  'Quantifiability',
  'Insights',
  '',
]];

type Shortcut = {
  code: string;
  shift?: boolean;
};

export const AdvancedLabelTypeShortcuts = new Map<string, Shortcut>([
  ['Workflow', { code: 'KeyW' }],
  ['Threat', { code: 'KeyT', shift: true }],
  ['Customer', { code: 'KeyC' }],
  ['Industry', { code: 'KeyI' }],
  ['LegalForm', { code: 'KeyL' }],
  ['Product', { code: 'KeyP' }],
  ['Transaction', { code: 'KeyT' }],
  ['Channel', { code: 'KeyC', shift: true }],
  ['Geography', { code: 'KeyG' }],
  ['Sub-Regions', { code: 'KeyS' }],
  ['Level', { code: 'KeyL', shift: true }],
  ['Quantifiability', { code: 'KeyQ' }],
  ['Insights', { code: 'KeyI', shift: true }],
]);
export const AdvancedLabelPickerDefaultActiveFilters = ['Pinned', 'Suggestions', 'Workflow'];

export const LabelTypesAlwaysInContext = (['Workflow']);

export const LabelIconsForGlasses = new Map<string, string>([
  ['Business', 'mdi-bank-circle'],
  ['Segment', 'mdi-account-circle'],
  ['Area', 'mdi-incognito-circle'],
  ['Product', 'mdi-incognito-circle'],
  ['Transaction', 'mdi-incognito-circle'],
]);

export const LabelTypesForGlasses = Array.from(LabelIconsForGlasses.keys());

export const LabelTypesForContextGlasses = ['Product', 'Transaction'];

export function getDefaultActiveFilters(): string[] {
  return uiConfig.state.config?.labelPicker?.defaultActiveFilters ?? AdvancedLabelPickerDefaultActiveFilters;
}

export function getAlwaysInContextTypes(): string[] {
  return uiConfig.state.config?.labelPicker?.alwaysInContext ?? LabelTypesAlwaysInContext;
}

export function getLabelIconForGlassesType(type: string): string {
  const glasses = uiConfig.state.config?.labelPicker?.glasses;
  if (glasses) {
    return glasses.find((g) => g.type === type)?.icon ?? 'mdi-circle';
  }
  return LabelIconsForGlasses.get(type) ?? 'mdi-circle';
}

export function getContextRelationTypes(): string[] {
  return uiConfig.state.config?.labelPicker?.contextRelationTypes ?? LabelTypesForContextGlasses;
}

export function getUseGlasses(): boolean {
  return uiConfig.state.config?.labelPicker?.useGlasses ?? true;
}

export function getUseContext(): boolean {
  return uiConfig.state.config?.labelPicker?.useContext ?? true;
}

export function getUsePotential(): boolean {
  return uiConfig.state.config?.labelPicker?.usePotential ?? true;
}

function getGlassesTypes(): string[] {
  const glasses = uiConfig.state.config?.labelPicker?.glasses;
  if (glasses && glasses.length > 0) {
    return glasses.map((g) => g.type);
  }
  return LabelTypesForGlasses;
}

export function labelTypeSorter(): (a: string, b: string) => number;
export function labelTypeSorter<T>(by: (t: T) => string): (a: T, b: T) => number;
export function labelTypeSorter<T>(by: (t: T) => string = (s) => s as unknown as string): (a: T, b: T) => number {
  return sortByExplicit(by, getLabelTypeOrder());
}
type SortStrategy = 'type' | 'alpha' | 'length' | 'natural';
export function labelSorter(): (a?: Label, b?: Label) => number;
export function labelSorter(strategy: SortStrategy[]): (a: Label, b: Label) => number;
export function labelSorter<T>(strategy: ((t: T) => SortStrategy[])): (a: Label, b: Label) => number;
export function labelSorter<T>(strategy: SortStrategy[], by: (t: T) => Label | undefined): (a: T, b: T) => number;
export function labelSorter<T>(
  sf: SortStrategy[] | ((t: T) => SortStrategy[]) = () => ['type', 'alpha'],
  by: (t: T) => Label | undefined = (l) => (l ? l as unknown as Label : undefined),
): (a: T, b: T) => number {
  const typeSorter = labelTypeSorter();
  const strategy = Array.isArray(sf) ? () => sf : sf;
  return (a: T, b: T) => {
    const al = by(a);
    const bl = by(b);
    if (!al) {
      return bl ? 1 : 0;
    } else if (!bl) {
      return al ? -1 : 0;
    }
    for (const strat of strategy(a)) {
      if (strat === 'type') {
        const byType = typeSorter(al.type, bl.type);
        if (byType !== 0) {
          return byType;
        }
      } else if (strat === 'length') {
        const lengthDelta = al.code.length - bl.code.length;
        if (lengthDelta !== 0) {
          return lengthDelta;
        }
      } else if (strat === 'alpha') {
        const alpha = al.code.localeCompare(bl.code);
        if (alpha !== 0) {
          return alpha;
        }
      } else if (strat === 'natural') {
        return al.id - bl.id;
      }
    }
    return al.id - bl.id;
  };
}

function createStore() {
  const state = Vue.observable({
    labels: [] as readonly Label[],
    edges: [] as readonly LabelEdge[],
    labelTypes: [] as readonly LabelType[],
    activeGlasses: [] as readonly Label[],
  });
  function index() {
    getters.lookup = uniqueIndex(state.labels || [], (l) => l.id);
    const edgesByLabelId: Map<number, number[]> = new Map();
    for (const e of state.edges) {
      edgesByLabelId.set(e.fromLabelId, [...(edgesByLabelId.get(e.fromLabelId) || []), e.toLabelId]);
      const from = getters.lookup.get(e.toLabelId);
      if (from?.type === 'Transaction') {
        edgesByLabelId.set(e.toLabelId, [...(edgesByLabelId.get(e.toLabelId) || []), e.fromLabelId]);
      }
    }
    getters.lookupWithEdges = uniqueIndex((state.labels || []).map((l) => ({
      ...l,
      edges: edgesByLabelId.get(l.id)?.map((e) => getters.lookup.get(e)).filter(notEmpty) || [],
    })), (l) => l.id);
    getters.glasses = uniqueIndex(findUniqueChildrenOfLabelRelations(
      [...getters.lookupWithEdges.values()],
      getGlassesTypes(),
    ), (l) => l.id);
    getters.type = memoize((type: string) => uniquelyIndexed(state.labels.filter((l) => l.type === type), (l) => l.code));
  }
  const getters = Vue.observable({
    lookup: new Map() as ReadonlyMap<number, Label>,
    lookupWithEdges: new Map() as ReadonlyMap<number, LabelWithEdges>,
    glasses: new Map() as ReadonlyMap<number, LabelWithEdges>,
    type: (_: string) => uniquelyIndexed<string, Label>([], (__) => ''),
  });
  const commit = {
    setLabels(ls: Label[], le: LabelEdge[], lt: LabelType[], activeGlasses: Label[]) {
      state.labelTypes = lt;
      state.labels = ls.sort(labelSorter());
      state.edges = le;
      state.activeGlasses = activeGlasses;
      index();
    },
    labelChange(label: Label) {
      const stateLabels = state.labels.filter((l) => (label.id === 0 ? l !== label : l.id !== label.id),
      );
      const ls = [...stateLabels, label];
      ls.sort(labelSorter());
      state.labels = ls;
      index();
    },
    labelTypeChange(labelType: LabelType) {
      const stateTypes = state.labelTypes.filter((lt) => lt.code !== labelType.code);
      state.labelTypes = [...stateTypes, labelType].sort((a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999));
    },
  };
  const dispatch = {
    async all(): Promise<readonly Label[]> {
      if (state.labels.length > 0) {
        return state.labels;
      }
      const [labels, edges, labelTypes] = await Promise.all([
        api.labels(),
        api.labelEdges(),
        api.labelTypes(),
      ]);
      const activeGlassesFromStorage = sessionStorage.getItem('alp.activeGlasses');
      if (activeGlassesFromStorage) {
        const activeGlasses = JSON.parse(activeGlassesFromStorage);
        commit.setLabels(labels, edges, labelTypes, activeGlasses);
      } else {
        commit.setLabels(labels, edges, labelTypes, []);
      }
      return labels;
    },
    async save(label: Label): Promise<Label> {
      const saved = await api.saveLabel(label);
      commit.labelChange(saved);
      return saved;
    },
    async saveLabelType(labelType: LabelType): Promise<LabelType> {
      const saved = await api.saveLabelType(labelType);
      commit.labelTypeChange(saved);
      return saved;
    },
    getLabelTypeByCode(code: string): LabelType | undefined {
      return state.labelTypes.find((lt) => lt.code === code);
    },
    saveActiveGlasses(activeGlasses: Label[]) {
      sessionStorage.setItem('alp.activeGlasses', JSON.stringify(activeGlasses));
      state.activeGlasses = activeGlasses;
    },
  };
  return {
    state, getters, dispatch,
  };
}

const labels = createStore();
export default labels;

export const findParentsOfLabelRelation = (
  search: readonly LabelWithEdges[],
  relationTypes: readonly string[],
): ReadonlyArray<LabelWithEdges> => search
  .filter((l) => l.edges && l.edges.find((lc) => relationTypes.includes(lc.type)));

export const findUniqueChildrenOfLabelRelations = (
  search: readonly LabelWithEdges[],
  relationTypes: readonly string[],
): ReadonlyArray<LabelWithEdges> => search
  .flatMap((l) => (l.edges ? l.edges.filter((lc) => relationTypes.includes(lc.type)) : []))
  .filter((l, idx, arr) => arr.findIndex((v) => v.id === l.id) === idx);

export const findRelatedTo = (
  label: LabelWithEdges,
  otherLabels: readonly Label[],
): ReadonlyArray<{label: Label; index: number}> => otherLabels
  .map((ol, idx) => (label.edges?.find((lc) => lc.id === ol.id) ? ({ label: ol, index: idx }) : undefined))
  .filter(notEmpty);

const oppositeProductTransactionType = (type: string): string | undefined => {
  if (type === 'Product') {
    return 'Transaction';
  }
  if (type === 'Transaction') {
    return 'Product';
  }
  return undefined;
};

export const findMissingRelationWarning = (
  label: LabelWithEdges,
  parents: readonly LabelWithEdges[],
): string | undefined => {
  const oppositeType = oppositeProductTransactionType(label.type);
  if (!oppositeType) {
    return undefined;
  }
  const hasRelatedOpposite = parents
    .some((p) => p.type === oppositeType && label.edges?.find((e) => e.id === p.id));
  return hasRelatedOpposite ? undefined : `No related ${oppositeType}`;
};

export function findLabelSuggestion(l: LabelWithStateAndSuggestions, type: LabelSuggestionType): LabelSuggestion | undefined
export function findLabelSuggestion(l: LabelWithStateAndSuggestions, type: 'potential'): PotentialSuggestion | undefined
export function findLabelSuggestion(l: LabelWithStateAndSuggestions, type: 'ml'): MlSuggestion | undefined
export function findLabelSuggestion(l: LabelWithStateAndSuggestions, type: LabelSuggestionType) {
  return l.suggestions?.find((ls) => ls.type === type);
}

export function isNewlyAdded(label: Label): boolean {
  const cutoff = Date.now() - 90 * 1000 * 60 * 60 * 24; // 90 days ago
  return !!label.created && label.created > cutoff;
}

/**
 * Creates a searchable string representation of a label for basic search
 * Used by LabelPicker component
 */
export function createSearchableString(label: Label, includeDescription = false, specialChars = false): string {
  let result = `${label.type} ${label.code}`.trim();
  if (includeDescription && label.description) {
    result += ` ${label.description}`;
  }
  if (specialChars) {
    result = result.replace(/[.\-_()[\]{},;:]/g, ' ');
  }
  return result;
}

/**
 * Unified search function for label filtering using basic string matching
 * @param labelList - Readonly array of labels to search through
 * @param searchTerm - The search term
 * @param options - Search options
 * @returns Filtered labels
 */
export function searchLabels<T extends Label>(
  labelList: readonly T[],
  searchTerm: string,
  options: {
    caseSensitive?: boolean;
    includeDescription?: boolean;
    specialChars?: boolean;
    fuzzy?: boolean;
  } = {},
): T[] {
  const {
    caseSensitive = false, includeDescription = false, specialChars = false, fuzzy = false,
  } = options;

  if (!searchTerm || searchTerm.trim().length === 0) {
    return [...labelList];
  }

  const normalize = (s: string) => (caseSensitive ? s : s.toLowerCase());
  const searchWords = normalize(searchTerm.trim()).split(/\s+/);

  const matchesWord = (searchWord: string, labelWords: string[]): boolean => {
    if (labelWords.some((lw) => lw.includes(searchWord))) {
      return true;
    }

    if (fuzzy && searchWord.length >= 3) {
      const allowedDistance = Math.max(1, Math.floor(searchWord.length / 5));
      return labelWords.some((lw) => editDistance(searchWord, lw) <= allowedDistance);
    }
    return false;
  };

  return labelList.filter((label) => {
    const searchableString = normalize(createSearchableString(label, includeDescription, specialChars));
    const labelWords = searchableString.split(/\s+/);
    return searchWords.every((sw) => matchesWord(sw, labelWords));
  });
}
